import { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetEntity, useUpdateEntity } from '../../hooks/use-entities';
import { Button } from '@@/ui/primitives';
import { SaveIcon, ArrowLeftIcon, AlertCircleIcon, SettingsIcon } from 'lucide-react';
import { SchemaBuilder as SchemaBuilderV2 } from '../../components/schema-builder-v2';
import { toast } from 'sonner';
import { EntityInfoForm } from './details';
import { AuthContext } from '@@/contexts/AuthContext';
import { cn } from '@@/lib/utils';
import { BaseSchema } from '../../schemas/entity';
import { Spinner } from '@@/ui/components/Spinner';

export function EntityViewPage() {
  const { getPathWithInstanceAlias } = useContext(AuthContext);
  const { entityId } = useParams<{
    entityId: string;
  }>();
  const navigate = useNavigate();

  const entity = useGetEntity(entityId || '', { enabled: !!entityId });

  const updateSchemaMutation = useUpdateEntity();
  const [hasChanges, setHasChanges] = useState(false);
  const [schemaChanges, setSchemaChanges] = useState<BaseSchema | null>(null);
  const [entityInfoChanges, setEntityInfoChanges] = useState<{ name: string; description?: string } | null>(null);

  const handleClose = () => {
    // Navigate back to the parent entity where the user came from
    navigate(getPathWithInstanceAlias(`/portal/dfc/browser/${entity?.data?.entityId}`));
  };

  const handleSchemaChange = (newSchema: BaseSchema) => {
    setSchemaChanges(newSchema);
    setHasChanges(true);
  };

  const handleEntityDetailChange = (updates: Partial<{ name: string; description?: string }>) => {
    if (!entity?.data) return;

    const currentInfo = entityInfoChanges || {
      name: entity.data.name,
      description: entity.data.description
    };

    const newInfo = { ...currentInfo, ...updates };
    setEntityInfoChanges(newInfo);
    setHasChanges(true);
  };

  const handleSaveSchema = async () => {
    if (!entity.data) {
      console.warn('No entity loaded');
      return;
    }

    if ((!schemaChanges && !entityInfoChanges) || !entityId) {
      console.warn('No changes to save');
      return;
    }

    try {
      const currentSchema = schemaChanges || entity.data.schema;
      const currentEntityInfo = entityInfoChanges || {
        name: entity.data.name,
        description: entity.data.description
      };

      await updateSchemaMutation.mutateAsync({
        entityId: entityId,
        schema: currentSchema,
        name: currentEntityInfo.name,
        permissions: entity.data.permissions,
        description: currentEntityInfo.description
      });

      await entity.refetch();

      setSchemaChanges(null);
      setEntityInfoChanges(null);
      setHasChanges(false);

      toast.success('Entity has been updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update Entity');
    }
  };

  const isUpdating = updateSchemaMutation.isPending || entity.isRefetching;

  return (
    <div className='h-full bg-white flex flex-col'>
      <div className='flex-1 flex flex-col h-full'>
        <div className='mx-2 flex-1 flex flex-col relative'>
          {entity.isLoading && (
            <div className='flex items-center justify-center h-full py-12'>
              <Spinner size='lg' className='stroke-1' />
            </div>
          )}
          {entity?.data && (
            <div className='h-full flex flex-col gap-2'>
              <div className='flex justify-between items-center px-2 pt-2'>
                <div className='flex-1 flex flex-col justify-center gap-4'>
                  <div className='flex flex-row items-center gap-2'>
                    <Button onClick={handleClose} variant='ghost' size='icon'>
                      <ArrowLeftIcon className='size-4' />
                    </Button>
                    <h2 className='text-lg font-semibold text-neutral-900 truncate'>{entity.data.name}</h2>
                  </div>
                </div>
                <div className={cn('flex items-center gap-2 rounded-full h-8', hasChanges ? 'bg-red-100' : '')}>
                  {hasChanges && (
                    <div className='flex gap-2 px-4'>
                      <div className='flex items-center gap-2 text-red-950 text-sm font-medium'>
                        <AlertCircleIcon className='size-4' />
                        You have unsaved changes
                      </div>
                    </div>
                  )}
                  <>
                    <Button
                      variant={hasChanges ? 'default' : 'ghost'}
                      size='sm'
                      onClick={handleSaveSchema}
                      disabled={isUpdating || !hasChanges}
                      className='text-sm! leading-snug font-medium! px-3! rounded-full'
                    >
                      <SaveIcon className='size-4' />
                      Save
                      {isUpdating ? <Spinner className='size-4 ml-2' /> : null}
                    </Button>
                  </>
                </div>
              </div>

              <div className='flex-1 flex flex-col border border-b-0 border-neutral-200 rounded-t-md overflow-hidden'>
                <div className='flex-1 overflow-auto'>
                  <div className='h-full flex flex-row divide-x divide-neutral-200'>
                    <div className='flex flex-col gap-3 w-90 py-4 px-4'>
                      <EntityInfoForm
                        entity={entity.data}
                        entityInfo={
                          entityInfoChanges || {
                            name: entity.data.name,
                            description: entity.data.description
                          }
                        }
                        onChange={handleEntityDetailChange}
                      />
                    </div>
                    <div className='flex-1 py-4 px-4'>
                      <div className='space-y-4'>
                        <div className='flex items-center gap-2'>
                          <SettingsIcon className='h-5 w-5 text-neutral-950' />
                          <h2 className='font-semibold text-neutral-950'>Schema</h2>
                        </div>
                        <p className='text-sm text-neutral-600'>
                          A Schema shapes the data structure for all items within this entity. You can modify the schema
                          by adding, removing, or updating properties.
                        </p>
                        <SchemaBuilderV2
                          schema={schemaChanges || entity.data.schema}
                          onChange={handleSchemaChange}
                          entityId={entity.data.entityId}
                          hasSubEntities={entity.data.childCount > 0}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
